import { handleActions } from 'redux-actions';
import { getFormValues } from 'redux-form';
import cloneDeep from 'lodash/cloneDeep';
import orderBy from 'lodash/orderBy';
import values from 'lodash/values';
import pick from 'lodash/pick';
import range from 'lodash/range';
import find from 'lodash/find';
import qs from 'qs';

import http from '../shared/http';
import endpoints from '../../config/endpoints';

import intl from '../shared/intl';
import checkPermissions from '../shared/check-permissions';

// actions types
const FETCH_DATA_SUCCESS = 'defined-referers/fetch-data-success';
const FETCH_DATA_ERROR = 'defined-referers/fetch-data-error';

const UPDATE_SUCCESS = 'defined-referers/update-success';
const UPDATE_ERROR = 'defined-referers/update-error';

const SORT_BY_COLUMN = 'defined-referers/table-sort-by-column';
const GOTO_PAGE_SUCCESS = 'defined-referers/goto-page-success';

const TOGGLE_PROGRESS_BAR = 'defined-referers/toggle-progress-bar';
const CHOOSE_FILTER = 'defined-referers/choose-filter';

// permissions
export const allowedPermissions = {
	view: 'defined_referers_list',
	create: 'defined_referers_update',
	edit: 'defined_referers_update',
	delete: 'defined_referers_delete'
};

// initial state
const initialState = {
	data: null,
	table: {
		columns: {
			domain: {
				index: 'domain',
				title: 'Domain',
				direction: 'desc'
			},
			referer: {
				index: 'referer',
				title: 'Example referer'
			},
			type: {
				index: 'type',
				title: 'Type',
				direction: 'desc',
				className: 'type'
			}
		},
		sortByColumn: {
			index: 'created_at',
			title: 'Created at',
			direction: 'desc'
		}
	},
	pagination: {
		currentPage: 1,
		perPage: 1000,
		total: null
	},
	statusesFilters: [
		{ name: 'not_defined', placeholder: 'Not defined', active: true },
		{ name: 'defined', placeholder: 'Defined', active: false }
	],
	showProgressBar: false,
	error: false
};

// actions
export const actions = {
	fetchData: () => (dispatch, getState) => {
		const { pagination: { perPage }, statusesFilters } = getState().definedReferers;
		const activeFilter = find(statusesFilters, { active: true }).name;
		request(0, perPage, activeFilter, getState)(dispatch);
	},

	updateRefererType: (val, row) => dispatch => {
		const { value } = val && val[0];

		return http({
			url: `${endpoints.DEFINED_REFERERS.BASE}/${row.id}`,
			method: 'put',
			actions: [UPDATE_SUCCESS, UPDATE_ERROR],
			data: pick(
				{
					...row,
					type: value
				},
				['domain', 'referer', 'type']
			),
			addToResponse: {
				id: row.id,
				type: value
			}
		})(dispatch);
	},

	sortByColumn: columnIndex => ({
		type: SORT_BY_COLUMN,
		payload: columnIndex
	}),

	gotoPage: page => (dispatch, getState) => {
		const { pagination: { perPage }, statusesFilters } = getState().definedReferers;
		const activeFilter = find(statusesFilters, { active: true }).name;
		const offset = (page - 1) * perPage;

		dispatch(actions.toggleProgressBar());

		request(offset, perPage, activeFilter, getState)(dispatch).then(action => {
			if (action.type === FETCH_DATA_SUCCESS) {
				dispatch({
					type: GOTO_PAGE_SUCCESS,
					payload: page
				});
			}
		});
	},

	toggleProgressBar: () => ({
		type: TOGGLE_PROGRESS_BAR
	}),

	chooseStatusesFilter: filterName => dispatch => {
		dispatch({
			type: CHOOSE_FILTER,
			payload: filterName
		});

		dispatch(actions.fetchData());
	},

	search: () => (dispatch, getState) => {
		const { pagination: { perPage }, statusesFilters } = getState().definedReferers;
		const activeFilter = find(statusesFilters, { active: true }).name;
		request(0, perPage, activeFilter, getState)(dispatch);
	}
};

// reducers
export default handleActions(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: action.payload.data.rows,
			pagination: {
				...state.pagination,
				total: action.payload.data.total
			},
			showProgressBar: false,
			error: false
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[SORT_BY_COLUMN]: (state, action) => {
			const { sortByColumn, columns } = state.table;
			const columnIndex = action.payload;
			const direction =
				sortByColumn['index'] == columnIndex
					? sortByColumn.direction === 'desc' ? 'asc' : 'desc'
					: columns[columnIndex].direction;
			return {
				...state,
				table: {
					...state.table,
					sortByColumn: {
						...columns[columnIndex],
						direction
					}
				}
			};
		},
		[GOTO_PAGE_SUCCESS]: (state, action) => ({
			...state,
			pagination: {
				...state.pagination,
				currentPage: action.payload
			}
		}),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[UPDATE_SUCCESS]: (state, action) => {
			return {
				...state,
				data: state.data.map(item => {
					if (item.id === action.meta.id) {
						return { ...item, type: action.meta.type };
					}
					return item;
				})
			};
		},
		[UPDATE_ERROR]: state => ({ ...state, error: true }),
		[CHOOSE_FILTER]: (state, action) => ({
			...state,
			statusesFilters: state.statusesFilters.map(entity => ({
				...entity,
				active: action.payload === entity.name
			})),
			showProgressBar: true
		})
	},
	cloneDeep(initialState)
);

// selectors
export const selectors = {
	getData: state => state.definedReferers.data,
	getTable: state => {
		const { table, data } = state.definedReferers;
		const rows = state.definedReferers.data ? sortColumns(data, table.sortByColumn) : [];
		return {
			headers: values(table.columns),
			rows: rows.slice(0, table.rowsNumber),
			sortedColumn: table.sortByColumn,
			rowsNumber: table.rowsNumber,
			totalRows: rows.length
		};
	},
	getRefererTypeOptions: state => {
		return state.root.lists['referer-types'].filter(i => i.id !== 'direct').map(i => ({ label: i.text, value: i.id }));
	},
	getPagination: state => {
		const { perPage, total, currentPage } = state.definedReferers.pagination;
		return {
			pages: range(Math.ceil(total / perPage)),
			currentPage
		};
	},
	getFilters: state => state.definedReferers.statusesFilters,
	getIntl: state => intl(state),
	getProgressBar: state => state.definedReferers.showProgressBar,
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.definedReferers.error
};

// functions
const sortColumns = (rows, sortedColumn) => {
	if (!rows) return null;
	return orderBy(
		rows,
		row => row[sortedColumn.index],
		sortedColumn.direction
	);
};

const request = (offset = 0, limit = 1000, status = 'not_defined', getState) => {
	const searchForm = getFormValues('definedReferersSearchForm')(getState());
	const search = searchForm && searchForm.search || '';

	const query = qs.stringify({
		limit,
		offset,
		search,
		status
	});

	return http({
		url: `${endpoints.DEFINED_REFERERS.BASE}?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
	});
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/defined-referers/index.js